<template>
  <div
    class="play-input"
    :class="[`play-input--${size}`, { 'is-error': !!error, 'is-disabled': disabled }]"
  >
    <label
      v-if="label"
      :for="fieldId"
      class="play-input__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="play-input__req"
        aria-hidden="true"
      >*</span>
    </label>

    <textarea
      v-if="textarea"
      :id="fieldId"
      class="play-input__field play-input__field--textarea"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows"
      :aria-invalid="!!error"
      v-bind="$attrs"
      v-on="fieldListeners"
    />
    <input
      v-else
      :id="fieldId"
      class="play-input__field"
      :type="type"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-invalid="!!error"
      v-bind="$attrs"
      v-on="fieldListeners"
    >

    <p
      v-if="error || hint"
      class="play-input__msg"
      :class="error ? 'play-input__msg--error' : 'play-input__msg--hint'"
    >
      {{ error || hint }}
    </p>
  </div>
</template>

<script>
/**
 * PlayInput — text field primitive for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces the
 * bootstrap-vue <b-form-group> + <b-form-input>/<b-form-textarea> combo in the
 * rebrand — label, field, validation message and the error color (--c-danger)
 * all live in one component.
 *
 * Renders an <input> (any native `type`) or a <textarea> via `textarea`.
 * Drive it with v-model; pass `error` for the invalid state + message, `hint`
 * for neutral helper text.
 *
 * VUE3-MIGRATION: two Vue 2-isms, both isolated:
 *   1. `model: { prop: 'value', event: 'input' }` — Vue 3 uses
 *      `modelValue` / `update:modelValue`. Change this block + the emit name.
 *   2. `fieldListeners` spreads `$listeners` (removed in Vue 3, merged into
 *      $attrs). On migration drop the spread; keep the local input handler.
 */
let uid = 0

export default {
  name: 'PlayInput',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    /** Native input type (ignored when `textarea` is set) */
    type: {
      type: String,
      default: 'text',
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [String, Number],
      default: 3,
    },
    placeholder: {
      type: String,
      default: '',
    },
    /** Error message — its presence drives the invalid styling */
    error: {
      type: String,
      default: '',
    },
    /** Neutral helper text shown when there is no error */
    hint: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Renders a `*` next to the label (visual only) */
    required: {
      type: Boolean,
      default: false,
    },
    /** Optional explicit id; auto-generated for label association otherwise */
    id: {
      type: String,
      default: '',
    },
  },
  data() {
    uid += 1
    return {
      generatedId: `play-input-${uid}`,
    }
  },
  computed: {
    fieldId() {
      return this.id || this.generatedId
    },
    /** Forward consumer listeners (@blur/@focus/…) while owning `input`. */
    fieldListeners() {
      return {
        ...this.$listeners,
        input: e => this.$emit('input', e.target.value),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-input {
  display: block;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-strong);
  }

  &__req { color: var(--c-danger); }

  &__field {
    display: block;
    width: 100%;
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-strong);
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius);
    box-shadow: var(--shadow-hard-sm);
    transition:
      box-shadow var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);
    -webkit-tap-highlight-color: transparent;

    &::placeholder { color: var(--text-muted); }

    &:focus {
      outline: none;
      box-shadow: var(--shadow-hard);
    }
  }

  &__field--textarea {
    resize: vertical;
    min-height: 84px;
    line-height: 1.45;
  }

  &__msg {
    margin: 6px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;

    &--error { color: var(--c-danger-strong); }
    &--hint { color: var(--text-muted); }
  }

  /* ── Sizes ── */
  &--sm .play-input__field { padding: 9px 13px;  font-size: 14px;   border-radius: var(--radius-sm); }
  &--md .play-input__field { padding: 12px 15px; font-size: 15px; }
  &--lg .play-input__field { padding: 15px 18px; font-size: 16px;   border-radius: var(--radius-lg); }

  /* ── Invalid ── */
  &.is-error .play-input__field {
    border-color: var(--c-danger);
    box-shadow: 0 4px 0 var(--c-danger-strong);

    &:focus { box-shadow: 0 6px 0 var(--c-danger-strong); }
  }

  /* ── Disabled ── */
  &.is-disabled {
    opacity: .55;

    .play-input__field { pointer-events: none; }
  }
}
</style>
